import { useState, useEffect, useCallback, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Search, AlertCircle } from 'lucide-react';
import { GeocodingResult, searchAddress, getTimezoneFromCoordinates } from './mapbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface AddressSearchProps {
  onSelect: (result: GeocodingResult) => void;
  onValidationChange?: (isValid: boolean) => void;
  placeholder?: string;
  value?: GeocodingResult;
}

export function AddressSearch({
  onSelect,
  onValidationChange,
  placeholder = 'Search address...',
  value,
}: AddressSearchProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value?.place_name || '');
  const [results, setResults] = useState<GeocodingResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasValidSelection, setHasValidSelection] = useState(!!value);
  const [showError, setShowError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Notify parent of validation state changes
  useEffect(() => {
    onValidationChange?.(hasValidSelection || inputValue.length === 0);
  }, [hasValidSelection, inputValue, onValidationChange]);

  // Debounced search function
  const debouncedSearch = useCallback(
    (() => {
      let timeout: NodeJS.Timeout;
      return (value: string) => {
        if (timeout) {
          clearTimeout(timeout);
        }
        timeout = setTimeout(async () => {
          if (value.length < 3) {
            setResults([]);
            setOpen(false);
            return;
          }

          setLoading(true);
          try {
            const searchResults = await searchAddress(value);
            setResults(searchResults);
            setOpen(true);
            setTimeout(() => inputRef.current?.focus(), 0);
          } catch (error) {
            console.error('Failed to search address:', error);
          } finally {
            setLoading(false);
          }
        }, 800);
      };
    })(),
    []
  );

  useEffect(() => {
    return () => {
      setResults([]);
      setOpen(false);
    };
  }, []);

  // Show error when user has typed but not selected
  const handleBlur = () => {
    if (inputValue.length > 0 && !hasValidSelection) {
      setShowError(true);
    }
  };

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              placeholder={placeholder}
              value={inputValue}
              onChange={(e) => {
                const value = e.target.value;
                setInputValue(value);
                setHasValidSelection(false);
                setShowError(false);
                debouncedSearch(value);
              }}
              onBlur={handleBlur}
              className={showError ? 'border-red-500 focus-visible:ring-red-500' : ''}
            />
            {loading ? (
              <Button variant="outline" size="icon" disabled type="button">
                <Loader2 className="h-4 w-4 animate-spin" />
              </Button>
            ) : (
              <Button
                variant="outline"
                size="icon"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  debouncedSearch(inputValue);
                }}
              >
                <Search className="h-4 w-4" />
              </Button>
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="p-0"
          align="start"
          side="bottom"
          sideOffset={4}
          autoFocus={false}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            setTimeout(() => inputRef.current?.focus(), 0);
          }}
        >
          <Command autoFocus={false}>
            <CommandList autoFocus={false}>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup autoFocus={false}>
                {results.map((result) => (
                  <CommandItem
                    key={result.place_name}
                    value={result.place_name}
                    onSelect={() => {
                      setInputValue(result.place_name);
                      setHasValidSelection(true);
                      setShowError(false);
                      const timezone = getTimezoneFromCoordinates(result.center[0], result.center[1]);
                      onSelect({ ...result, timezone });
                      setOpen(false);
                    }}
                  >
                    {result.place_name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {showError && (
        <div className="flex items-center gap-2 text-sm text-red-500">
          <AlertCircle className="h-4 w-4" />
          <span>Please select an address from the suggestions</span>
        </div>
      )}
    </div>
  );
}
